/**
 * CRM Import — bring data over from HoneyBook / Dubsado / Studio Ninja
 * / 17hats / Bonsai / generic CSV.
 *
 * Two pieces:
 *   1. parseCsv() — RFC 4180-ish parser handling quoted fields and
 *      newlines inside quotes. No external deps.
 *   2. SOURCE_PRESETS — per-CRM column mappings so the user picks
 *      "HoneyBook" and we know that "Client Name" maps to `name`,
 *      "Phone Number" maps to `phone`, etc. Falls back to manual
 *      mapping for columns not in the preset.
 *
 * Targets supported in v1: clients (the main use case). Inquiries and
 * projects come in v2 once we sort out parent-child rows.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';

// ─── CSV parser ──────────────────────────────────────────────────

/**
 * Parse a CSV string into header + rows. Handles:
 *   - quoted fields with embedded commas
 *   - escaped quotes ("" inside a quoted field)
 *   - newlines inside quoted fields
 *   - trailing whitespace
 *   - CSV-IMPORT-1 (Oct 2): semicolon- and tab-separated files (Excel
 *     saves "CSV" with semicolons wherever the comma is the decimal
 *     mark), told apart by the header line; and old-Mac lone-CR line ends
 */
export function parseCsv(input: string): { headers: string[]; rows: string[][] } {
  const text = input.replace(/^﻿/, ''); // strip BOM
  const delim = detectDelimiter(text);
  const out: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++; // skip the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === delim) {
        row.push(field);
        field = '';
      } else if (ch === '\n' || (ch === '\r' && text[i + 1] !== '\n')) {
        // \n, or a lone \r (classic Mac); \r\n is picked up at the \n.
        row.push(field);
        out.push(row);
        row = [];
        field = '';
      } else if (ch === '\r') {
        // skip (Windows line endings — \n picks it up)
      } else {
        field += ch;
      }
    }
  }
  // Flush trailing field/row
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    out.push(row);
  }

  if (out.length === 0) return { headers: [], rows: [] };
  const headers = (out[0] ?? []).map((h) => h.trim());
  const rows = out.slice(1).filter((r) => r.some((cell) => cell.trim().length > 0));
  return { headers, rows };
}

/** The header line's separator: comma, semicolon or tab — whichever it
 *  has most of, outside quotes. Comma when there's no telling. */
function detectDelimiter(text: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (ch === '\n' || ch === '\r')) break;
    else if (!inQuotes && ch in counts) counts[ch] += 1;
  }
  const best = (Object.keys(counts) as (keyof typeof counts)[]).sort((a, b) => counts[b] - counts[a])[0];
  return counts[best] > 0 ? best : ',';
}

// ─── Source presets ──────────────────────────────────────────────

/**
 * Target columns Orbit understands when importing into `clients`.
 * Strings here are arbitrary keys we use internally; the actual DB
 * column names live in TARGET_TO_DB_COLUMN below.
 */
export type ClientImportField = 'name' | 'email' | 'phone' | 'brand' | 'notes';

export const TARGET_TO_DB_COLUMN: Record<ClientImportField, string> = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  brand: 'brand',
  notes: 'notes',
};

export interface SourcePreset {
  /// Display name for the source picker.
  name: string;
  /// Array of header→target hints. Each hint is matched
  /// case-insensitively against the CSV's column headers.
  /// First match wins, so list more specific keys first.
  mapping: { headerHints: string[]; target: ClientImportField | null }[];
  /// One-line description shown under the source name.
  description: string;
}

export const SOURCE_PRESETS: Record<string, SourcePreset> = {
  honeybook: {
    name: 'HoneyBook',
    description: 'Export from HoneyBook → Contacts → Export to CSV',
    mapping: [
      { headerHints: ['client name', 'name', 'full name'], target: 'name' },
      { headerHints: ['email', 'email address'], target: 'email' },
      { headerHints: ['phone', 'phone number'], target: 'phone' },
      { headerHints: ['project type', 'project'], target: 'brand' },
      { headerHints: ['notes', 'description', 'note'], target: 'notes' },
    ],
  },
  dubsado: {
    name: 'Dubsado',
    description: 'Export from Dubsado → Reports → Contacts CSV',
    mapping: [
      { headerHints: ['first name', 'firstname'], target: 'name' },
      { headerHints: ['email'], target: 'email' },
      { headerHints: ['phone', 'phone number'], target: 'phone' },
      { headerHints: ['project type'], target: 'brand' },
      { headerHints: ['notes', 'description'], target: 'notes' },
    ],
  },
  studio_ninja: {
    name: 'Studio Ninja',
    description: 'Export from Studio Ninja → Reports → Clients',
    mapping: [
      { headerHints: ['name', 'client name'], target: 'name' },
      { headerHints: ['email'], target: 'email' },
      { headerHints: ['phone', 'mobile'], target: 'phone' },
      { headerHints: ['venue', 'event location'], target: 'brand' },
      { headerHints: ['notes', 'enquiry notes'], target: 'notes' },
    ],
  },
  '17hats': {
    name: '17hats',
    description: 'Export from 17hats → Contacts → Export',
    mapping: [
      { headerHints: ['name', 'contact name'], target: 'name' },
      { headerHints: ['email'], target: 'email' },
      { headerHints: ['phone'], target: 'phone' },
      { headerHints: ['project type'], target: 'brand' },
      { headerHints: ['notes'], target: 'notes' },
    ],
  },
  bonsai: {
    name: 'Bonsai',
    description: 'Export from Bonsai → Clients → Export',
    mapping: [
      { headerHints: ['client name', 'name'], target: 'name' },
      { headerHints: ['email'], target: 'email' },
      { headerHints: ['phone'], target: 'phone' },
      { headerHints: ['notes', 'description'], target: 'notes' },
    ],
  },
  pixieset: {
    name: 'Pixieset',
    description: 'Export from Pixieset Studio Manager → Clients → Export CSV',
    mapping: [
      { headerHints: ['name', 'client name', 'first name'], target: 'name' },
      { headerHints: ['email', 'email address'], target: 'email' },
      { headerHints: ['phone', 'phone number'], target: 'phone' },
      // Pixieset organizes by event type; surface that as the brand/category
      // so studios can re-segment in Orbit later.
      { headerHints: ['event type', 'session type', 'collection'], target: 'brand' },
      { headerHints: ['notes', 'description', 'tags'], target: 'notes' },
    ],
  },
  generic: {
    name: 'Generic CSV',
    description: 'Any spreadsheet — we\'ll auto-detect common columns',
    mapping: [
      { headerHints: ['name', 'full name', 'client name', 'contact name'], target: 'name' },
      { headerHints: ['email', 'email address'], target: 'email' },
      { headerHints: ['phone', 'phone number', 'mobile', 'cell'], target: 'phone' },
      { headerHints: ['brand', 'company', 'organization', 'venue', 'project type'], target: 'brand' },
      { headerHints: ['notes', 'description', 'comments'], target: 'notes' },
    ],
  },
};

/**
 * Auto-map CSV headers to target fields using the source's preset.
 * Returns a `Record<csvHeader, target | null>`. Headers without a
 * preset match are returned with target=null so the user can map
 * them manually.
 */
export function autoMapHeaders(
  headers: string[],
  preset: SourcePreset,
): Record<string, ClientImportField | null> {
  const out: Record<string, ClientImportField | null> = {};
  for (const header of headers) {
    const lower = header.toLowerCase().trim();
    let matched: ClientImportField | null = null;
    for (const rule of preset.mapping) {
      if (rule.headerHints.some((h) => lower === h || lower.includes(h))) {
        matched = rule.target;
        break;
      }
    }
    out[header] = matched;
  }
  return out;
}

// ─── Row → row transformer + validators ─────────────────────────

interface ClientRow {
  name: string;
  email: string | null;
  phone: string | null;
  brand: string | null;
  notes: string | null;
}

/**
 * Apply the column mapping to a CSV row, producing a typed row
 * ready to insert. Returns null if the row has no name (we won't
 * import nameless rows — they're almost always blank lines).
 */
export function buildClientRow(
  headers: string[],
  csvRow: string[],
  mapping: Record<string, ClientImportField | null>,
): ClientRow | null {
  const row: Partial<ClientRow> = {};
  // Concatenate "First Name" + "Last Name" → name when both are mapped to name
  const nameParts: string[] = [];

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    const target = mapping[header];
    const value = (csvRow[i] ?? '').trim();
    if (!target || !value) continue;

    if (target === 'name') {
      nameParts.push(value);
    } else {
      row[target] = value as any;
    }
  }
  const name = nameParts.join(' ').trim();
  if (!name) return null;

  return {
    name,
    email: row.email ?? null,
    phone: row.phone ?? null,
    brand: row.brand ?? null,
    notes: row.notes ?? null,
  };
}

// ─── Batch insert ───────────────────────────────────────────────

export interface ImportResult {
  inserted: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

export const useImportClients = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  return useMutation({
    mutationFn: async ({
      headers,
      rows,
      mapping,
    }: {
      headers: string[];
      rows: string[][];
      mapping: Record<string, ClientImportField | null>;
    }): Promise<ImportResult> => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');

      const toInsert: (ClientRow & { org_id: string })[] = [];
      const errors: { row: number; message: string }[] = [];
      let skipped = 0;

      rows.forEach((csvRow, idx) => {
        try {
          const built = buildClientRow(headers, csvRow, mapping);
          if (!built) {
            skipped += 1;
            return;
          }
          toInsert.push({ ...built, org_id: orgId });
        } catch (e: any) {
          errors.push({ row: idx + 2, message: e?.message ?? 'Unknown error' });
        }
      });

      if (toInsert.length === 0) {
        return { inserted: 0, skipped, errors };
      }

      // Batch in chunks so we don't blow the request size limit on
      // a 5000-row export.
      const CHUNK = 200;
      let inserted = 0;
      for (let i = 0; i < toInsert.length; i += CHUNK) {
        const slice = toInsert.slice(i, i + CHUNK);
        const { error } = await supabase.from('clients').insert(slice);
        if (error) {
          errors.push({
            row: i + 2,
            message: `Batch ${Math.floor(i / CHUNK) + 1}: ${error.message}`,
          });
        } else {
          inserted += slice.length;
        }
      }

      return { inserted, skipped, errors };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
};
